/**
 * 把當前頁面轉去 Safari 開啟。
 *
 * 用途只有一個：iOS 上非 Safari 的瀏覽器要加入主畫面時，把使用者送到唯一保證
 * 做得到的地方。
 *
 * **`x-safari-https:` 是非官方的。**iOS 沒有任何官方 API 能指定用 Safari 開啟
 * 網址（只有 `http(s):` 會走預設瀏覽器，而預設瀏覽器正是這裡的問題）。這個 scheme
 * 實測到目前的 iOS 仍然有效，但 Apple 從未在文件裡承認過它，隨時可能失效——因此
 * 這支**一定要有退路**，不能假設它會成功。
 *
 * 退路是把網址複製起來，請使用者自己在 Safari 貼上。醜，但永遠有效。
 */

export type OpenInSafariResult =
  /** 已跳離本頁（Safari 接手了） */
  | 'opened'
  /** 沒跳成，但網址已複製到剪貼簿 */
  | 'copied'
  /** 沒跳成，也複製不了（無痕模式或權限被擋） */
  | 'failed';

/** 等多久判定「沒跳走」。跳轉成功時頁面會在數百毫秒內隱藏，1.2 秒足夠且不至於呆等。 */
const SWITCH_TIMEOUT_MS = 1200;

export async function openInSafari(
  url: string = window.location.href,
): Promise<OpenInSafariResult> {
  /* 只有 https 頁面能換；本機 http 開發時直接走複製那條路 */
  const safariUrl = url.startsWith('https:')
    ? url.replace(/^https:/, 'x-safari-https:')
    : null;

  if (safariUrl) {
    const switched = waitForPageHidden();
    window.location.href = safariUrl;

    if (await switched) return 'opened';
  }

  /*
   * 走到這裡代表 scheme 沒被系統接手（頁面還在）。**不要再試第二次**——重複導向
   * 只會讓 Chrome 跳出「無法開啟」的對話框，那比安靜地退回複製更糟。
   */
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/**
 * 頁面是否在時限內被切走。
 *
 * 判斷「跳轉成功了嗎」沒有直接的訊號，只能看本頁有沒有被隱藏：切換 app 會觸發
 * `visibilitychange`（iOS 上也可能是 `pagehide`），兩個都聽，先到先算。
 */
function waitForPageHidden(): Promise<boolean> {
  return new Promise((resolve) => {
    const settle = (hidden: boolean) => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      resolve(hidden);
    };

    const onVisibilityChange = () => {
      if (document.hidden) settle(true);
    };
    const onPageHide = () => settle(true);

    const timer = setTimeout(() => settle(false), SWITCH_TIMEOUT_MS);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  });
}
